import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@/lib/cn";

export const Tabs = TabsPrimitive.Root;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "flex items-center border-b border-border overflow-x-auto",
      // Vertical orientation: stack the triggers, drop the horizontal-tab
      // assumptions. `overflow-x-auto` alone forces overflow-y to `auto`
      // (CSS: a non-visible axis makes the visible one compute to auto),
      // which spawned a phantom vertical scrollbar on the rail.
      "data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:overflow-visible",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // `inline-flex` is load-bearing: without it `justify-*`/`gap-*` are inert
      // and a trigger with an icon + label falls back to the button's centered
      // inline layout (icon and label drift apart — the vertical-rail bug).
      "inline-flex items-center gap-2 px-3 h-9 text-sm text-muted-foreground border-b-2 border-transparent -mb-px",
      "hover:text-foreground data-[state=active]:text-foreground data-[state=active]:border-accent",
      // Vertical orientation: full-width left-aligned rows; the active marker
      // becomes a LEFT border; drop the bottom border + the -1px overlap.
      "data-[orientation=vertical]:mb-0 data-[orientation=vertical]:w-full data-[orientation=vertical]:justify-start data-[orientation=vertical]:rounded-md data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:border-l-2 data-[orientation=vertical]:data-[state=active]:bg-elevated",
      "focus-visible:outline focus-visible:outline-1 focus-visible:outline-ring",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("focus-visible:outline-none", className)}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
